import { deriveNotices, type NoticeLink } from '@veem/crm-schema'
import type { Role } from '@veem/schema'
import { productById } from '../data/catalog.ts'
import { invoiceStatus, invoiceTotal } from '../data/invoices.ts'
import type { ProtoOrder } from '../data/orders.ts'
import { personById } from '../data/people.ts'
import { compare, isPositive, sub } from '../lib/decimal.ts'
import { toIso } from '../lib/due.ts'
import { formatMoney, formatQuantity, TODAY } from '../lib/format.ts'
import { BASE } from '../lib/nav.tsx'
import { isProductionLead, managesWorkCenter } from '../lib/permissions.ts'
import { engineeringContext, engineeringTasks, productOf, worksOn } from './engineering.ts'
import { engineeringReworkRequests } from './engineering-rework.ts'
import { customerOf, uninvoicedOf } from './invoices.ts'
import { proto } from './store.ts'

export type InboxKind =
  | 'crm_task'
  | 'handoff'
  | 'engineering'
  | 'engineering_unassigned'
  | 'rework'
  | 'notice'
  | 'issue'
  | 'shortage'
  | 'critical_stock'
  | 'purchase_request'
  | 'receipt'
  | 'stage'
  | 'internal_stage'
  | 'ship'
  | 'invoice'
  | 'payment'
  | 'approval'
  | 'supplier_payment'

export interface InboxItem {
  id: string
  kind: InboxKind
  title: string
  context: string
  dueAt: string | null
  /** Who or what created the work, shown as "від …". */
  from: string
  href: string
  dealId?: string
  refId?: string
  /** For a stage of an internal order: which stage of the order in `refId`. */
  stageKey?: string
}

const orderHref = (order: ProtoOrder) => `${BASE}/orders/${order.deal.id}`

const productSummary = (order: ProtoOrder): string => {
  const line = order.lines[0]
  if (!line) return order.deal.name
  const product = productById(line.productId)
  const more = order.lines.length > 1 ? ` і ще ${order.lines.length - 1}` : ''
  return `${product.name} · ${formatQuantity(line.quantity, product.unit)}${more}`
}

const salesItems = (personId: string): InboxItem[] => {
  const items: InboxItem[] = []
  for (const order of proto.state.orders) {
    if (order.deal.managerId !== personId) continue
    if (order.deal.stage === 'approval' && !order.production) {
      items.push({
        id: `handoff-${order.deal.id}`,
        kind: 'handoff',
        title: 'Клієнт погодив? Передайте замовлення у виробництво',
        context: `${order.deal.clientName} · ${productSummary(order)}`,
        dueAt: order.deal.expectedCloseAt,
        from: 'Статус «Узгодження»',
        href: orderHref(order),
        dealId: order.deal.id,
      })
    }
  }
  return items
}

/** Engineering requests are assigned by engineers, the general production leads and admin. */
const assignsEngineering = (role: Role) => ['engineer', 'chief_engineer', 'admin'].includes(role)

const engineeringItems = (personId: string, role: Role): InboxItem[] =>
  engineeringTasks().flatMap((task): InboxItem[] => {
    const request = task.request
    if (request.state === 'done') return []
    const mine = worksOn(request, personId)
    const unassigned = !request.engineerId && assignsEngineering(role)
    // A separate job is just a task: show its own name, not the tech-card wording.
    const separate = request.source.kind === 'manual'
    if (!mine && !unassigned) return []
    return [
      {
        id: `${mine ? 'eng' : 'eng-unassigned'}-${task.id}`,
        kind: mine ? 'engineering' : 'engineering_unassigned',
        title: mine
          ? separate
            ? productOf(task).name
            : 'Розробити креслення і технологічну карту'
          : separate
            ? `Призначте конструктора: ${productOf(task).name}`
            : 'Призначте конструктора на новий виріб',
        context: separate
          ? engineeringContext(task)
          : `${engineeringContext(task)} · ${productOf(task).name}`,
        dueAt: request.dueAt,
        from: personById(request.requestedById).name,
        href: `${BASE}/engineering/${task.id}?engineer=${encodeURIComponent(request.engineerId ?? 'unassigned')}`,
        dealId: task.order?.deal.id,
        refId: task.id,
      },
    ]
  })

/** «На доопрацювання» from the shop goes to the engineer it names. Same ids as the server. */
const reworkItems = (personId: string): InboxItem[] =>
  engineeringReworkRequests()
    .filter((request) => request.designerId === personId)
    .map((request) => ({
      id: `rework-${request.id}`,
      kind: 'rework' as const,
      title: `Доопрацювати: ${request.productName}`,
      context: `${request.orderNumber} · ${request.section}`,
      dueAt: request.deadline,
      from: request.actorName,
      href: `${BASE}/production/${request.orderId}?tab=rework`,
      refId: request.id,
    }))

const warehouseItems = (): InboxItem[] => {
  const items: InboxItem[] = []
  for (const order of proto.state.orders) {
    const production = order.production
    if (!production) continue
    if (production.supply === 'reserved') {
      items.push({
        id: `issue-${order.deal.id}`,
        kind: 'issue',
        title: `Видати комплект матеріалів для ${production.number}`,
        context: productSummary(order),
        dueAt: toIso(TODAY, '16:00'),
        from: 'Автоматично після резерву',
        href: `${BASE}/production/zm-${production.number.replace(/\D/g, '')}`,
        dealId: order.deal.id,
      })
    }
    if (production.supply === 'shortage') {
      const short = production.materials.filter((item) => isPositive(item.shortage))
      const requested = short.every((item) =>
        proto.state.purchaseRequests.some(
          (request) =>
            request.materialId === item.materialId &&
            request.orderNumber === production.number &&
            (request.state === 'open' ||
              proto.state.expected.some(
                (receipt) =>
                  receipt.materialId === item.materialId &&
                  receipt.status !== 'received' &&
                  receipt.supplyOrder === request.supplyOrder,
              )),
        ),
      )
      if (!requested) {
        items.push({
          id: `shortage-${order.deal.id}`,
          kind: 'shortage',
          title: `Не вистачає матеріалу для ${production.number}`,
          context: short
            .map((item) => `${item.materialName}: ${formatQuantity(item.shortage, item.baseUnit)}`)
            .join(', '),
          dueAt: toIso(TODAY, '12:00'),
          from: 'Автоматично при резерві',
          href: `${BASE}/warehouse/purchasing`,
          dealId: order.deal.id,
        })
      }
    }
  }
  const shortMaterials = new Set(
    proto.state.orders.flatMap((order) =>
      order.production?.supply === 'shortage'
        ? order.production.materials
            .filter((item) => isPositive(item.shortage))
            .map((item) => item.materialId)
        : [],
    ),
  )
  for (const material of proto.state.materials) {
    if (shortMaterials.has(material.id)) continue
    const available = sub(material.physical, material.reserved)
    const requested = proto.state.purchaseRequests.some(
      (request) => request.materialId === material.id && request.state === 'open',
    )
    if (compare(available, material.critical) <= 0 && !isPositive(material.ordered) && !requested) {
      items.push({
        id: `critical-${material.id}`,
        kind: 'critical_stock',
        title: `Критичний залишок: ${material.name}`,
        context: `Доступно ${formatQuantity(available, material.unit)}, поріг ${formatQuantity(material.critical, material.unit)}`,
        dueAt: null,
        from: 'Контроль залишків',
        href: `${BASE}/warehouse/purchasing`,
        refId: material.id,
      })
    }
  }
  for (const receipt of proto.state.expected.filter((item) => item.status !== 'received')) {
    const material = proto.state.materials.find((candidate) => candidate.id === receipt.materialId)
    if (!material) continue
    items.push({
      id: `receipt-${receipt.id}`,
      kind: 'receipt',
      title: `Прийняти надходження: ${material.name}`,
      context: `${formatQuantity(receipt.quantity, material.unit)} · ${receipt.supplier || 'Постачальника не обрано'}`,
      dueAt: receipt.expectedAt ? toIso(receipt.expectedAt, '10:00') : null,
      from: receipt.status === 'in_transit' ? 'У дорозі' : 'Замовлено',
      href: `${BASE}/warehouse/purchasing?tab=ordered&receive=${receipt.id}`,
      refId: receipt.id,
    })
  }
  return items
}

/** «Потрібно закупити» from sales, engineering, production and the warehouse itself. */
const purchaseRequestItems = (): InboxItem[] =>
  proto.state.purchaseRequests
    .filter((request) => request.state === 'open')
    .map((request) => {
      const material = proto.state.materials.find((item) => item.id === request.materialId)
      return {
        id: `purchase-request-${request.id}`,
        kind: 'purchase_request' as const,
        title: `Потрібно закупити: ${material?.name ?? request.title ?? 'позиція'}`,
        context: [
          formatQuantity(request.quantity, material?.unit ?? request.unit ?? undefined),
          request.orderNumber,
          request.reason,
        ]
          .filter(Boolean)
          .join(' · '),
        dueAt: request.neededBy ? toIso(request.neededBy, '12:00') : null,
        from: personById(request.requestedById).name,
        href: `${BASE}/warehouse/purchasing?request=${request.id}`,
        refId: request.id,
      }
    })

const stageItems = (personId: string, role: Role): InboxItem[] =>
  proto.state.orders.flatMap((order): InboxItem[] => {
    const production = order.production
    if (production?.supply !== 'issued' && production?.supply !== 'none') return []
    const stage = production.stages.find((candidate) => candidate.status === 'active')
    if (!stage) return []
    const mine = stage.assigneeId === personId
    const unassignedForLead =
      isProductionLead(role) && !stage.assigneeId && managesWorkCenter(role, stage.workCenter)
    if (!mine && !unassignedForLead) return []
    return [
      {
        id: `stage-${order.deal.id}-${stage.key}`,
        kind: 'stage',
        title: mine
          ? `${stage.label}: ${production.number}`
          : `Призначте виконавця на «${stage.label}»`,
        context: `${productSummary(order)} · готово ${formatQuantity(stage.good)} з ${formatQuantity(stage.planned)}`,
        dueAt: toIso(production.deadline, '18:00'),
        from: stage.workCenter,
        href: `${BASE}/production/queue?task=zm-${production.number.replace(/\D/g, '')}`,
        dealId: order.deal.id,
        refId: stage.key,
      },
    ]
  })

/** An internal order's stage reaches the person named on it, whatever their role. */
const internalStageItems = (personId: string): InboxItem[] =>
  proto.state.internalOrders.flatMap((order) =>
    order.stages
      .filter((stage) => stage.status === 'active' && stage.assigneeId === personId)
      .map(
        (stage): InboxItem => ({
          id: `stage-${order.id}-${stage.key}`,
          kind: 'internal_stage',
          title: `${stage.label}: ${order.number}`,
          context: order.productName,
          dueAt: toIso(order.deadline, '18:00'),
          from: personById(order.requestedById).name,
          href: `${BASE}/production/${order.id}`,
          refId: order.id,
          stageKey: stage.key,
        }),
      ),
  )

const logisticsItems = (): InboxItem[] =>
  proto.state.orders.flatMap((order): InboxItem[] =>
    order.shipment?.status === 'ready'
      ? [
          {
            id: `ship-${order.deal.id}`,
            kind: 'ship',
            title: `Відвантажити ${order.production?.number ?? order.deal.name}`,
            context: `${order.deal.clientName} · ${productSummary(order)}`,
            dueAt: order.production ? toIso(order.production.deadline, '12:00') : null,
            from: 'Виробництво завершено',
            href: orderHref(order),
            dealId: order.deal.id,
          },
        ]
      : [],
  )

const accountingItems = (): InboxItem[] => {
  const items: InboxItem[] = []
  for (const order of proto.state.orders) {
    if (
      order.shipment &&
      order.shipment.status !== 'ready' &&
      compare(uninvoicedOf(order), '0') > 0
    ) {
      items.push({
        id: `invoice-${order.deal.id}`,
        kind: 'invoice',
        title: `Виставити рахунок: ${order.deal.clientName}`,
        context: `${order.production?.number ?? ''} · ${order.deal.name}`,
        dueAt: toIso(TODAY, '18:00'),
        from: 'Відвантажено',
        href: orderHref(order),
        dealId: order.deal.id,
      })
    }
  }
  for (const invoice of proto.state.invoices) {
    const status = invoiceStatus(invoice)
    if (status === 'paid') continue
    items.push({
      id: `payment-${invoice.id}`,
      kind: 'payment',
      title: `Перевірити оплату рахунку ${invoice.number}`,
      context: `${customerOf(invoice)?.name ?? ''} · ${formatMoney(invoiceTotal(invoice), invoice.currency)}`,
      dueAt: toIso(invoice.dueAt, '18:00'),
      from: status === 'overdue' ? 'Прострочено' : 'Очікує оплати',
      href: `${BASE}/finance/invoices?id=${invoice.id}`,
      dealId: invoice.dealId ?? undefined,
      refId: invoice.id,
    })
  }
  for (const request of proto.state.paymentRequests) {
    if (request.status === 'pending')
      items.push({
        id: `approval-${request.id}`,
        kind: 'approval',
        title: `Погодити заявку на оплату: ${request.supplier}`,
        context: request.purpose,
        dueAt: toIso(request.dueAt, '18:00'),
        from: personById(request.requestedById).name,
        href: `${BASE}/finance/payment-requests?id=${request.id}`,
        refId: request.id,
      })
    if (request.status === 'approved')
      items.push({
        id: `pay-supplier-${request.id}`,
        kind: 'supplier_payment',
        title: `Оплатити: ${request.supplier}`,
        context: `${request.purpose} · ${formatMoney(request.amount)}`,
        dueAt: toIso(request.dueAt, '18:00'),
        from: 'Заявку погоджено',
        href: `${BASE}/finance/payment-requests?id=${request.id}`,
        refId: request.id,
      })
  }
  return items
}

const noticeHref = (link: NoticeLink, cancelled: boolean): string => {
  if (link.type === 'task') return `${BASE}/inbox?view=done&task=${encodeURIComponent(link.id)}`
  // A cancelled engineering job no longer has a page.
  if (link.type === 'engineering')
    return cancelled ? `${BASE}/engineering` : `${BASE}/engineering/${link.id}`
  return `${BASE}/production/${link.id}`
}

/** «Готово» for whoever handed work out, «Скасовано» for whoever was doing it. */
const noticeItems = (personId: string): InboxItem[] =>
  deriveNotices(proto.state, personId).map((notice) => ({
    id: notice.id,
    kind: 'notice' as const,
    title: notice.title,
    context: notice.context,
    dueAt: null,
    from: notice.fromId ? personById(notice.fromId).name : 'Система',
    href: noticeHref(notice.link, notice.kind === 'cancelled'),
  }))

/**
 * Work the system hands to a person: a hand-off, an issue request, a shortage. These sit next to
 * the person's own tasks in "Мої завдання" and disappear once the step is done.
 */
export const inboxFor = (role: Role, personId: string): InboxItem[] => {
  const items: InboxItem[] = []
  if (role === 'sales' || role === 'admin') items.push(...salesItems(personId))
  if (assignsEngineering(role))
    items.push(...engineeringItems(personId, role), ...reworkItems(personId))
  if (role === 'storekeeper') items.push(...warehouseItems(), ...purchaseRequestItems())
  if (isProductionLead(role) || role === 'worker') items.push(...stageItems(personId, role))
  if (role === 'production' || role === 'production_head')
    items.push(...warehouseItems().filter((item) => item.kind === 'shortage'))
  if (role === 'logistician') items.push(...logisticsItems())
  if (role === 'accountant') items.push(...accountingItems())
  if (role === 'admin') {
    items.push(...accountingItems().filter((item) => item.kind === 'approval'))
  }
  items.push(...internalStageItems(personId), ...noticeItems(personId))
  const dismissed = new Set(proto.state.dismissed)
  return items
    .filter((item) => !dismissed.has(item.id))
    .sort((left, right) => (left.dueAt ?? '9999').localeCompare(right.dueAt ?? '9999'))
}

export const openTasksFor = (personId: string) =>
  proto.state.tasks.filter((task) => task.status === 'open' && task.assigneeId === personId)

/** The number on the sidebar: own open tasks plus system work. */
export const inboxCount = (role: Role, personId: string): number =>
  openTasksFor(personId).length + inboxFor(role, personId).length

/** The ids the server tracks for "нове": own open tasks as `task-<id>`, system work as is. */
export const taskInboxId = (taskId: string) => `task-${taskId}`
export const inboxIds = (role: Role, personId: string): string[] => [
  ...openTasksFor(personId).map((task) => taskInboxId(task.id)),
  ...inboxFor(role, personId).map((item) => item.id),
]
